/**
 * Collection grid view - displays cards as image thumbnails
 * Supports selection mode with checkbox overlays for bulk operations
 */
import clsx from 'clsx';
import { memo, type ReactElement } from 'react';
import { IoCheckbox, IoSquareOutline, IoStar, IoStarOutline } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import FlippableCardImage from '@/components/FlippableCardImage/FlippableCardImage';
import { useIsStarred, useToggleStar } from '@/features/starred/hooks/api';
import { getDisplayImageUris, getOracleId } from '@/lib/cardUtils';
import type { EnrichedCollectionCard } from '@/types/card';
import useSelectedCard from '../hooks/useSelectedCard';
import QuantityBadge from './QuantityBadge';

export interface CollectionGridProps {
  cards: EnrichedCollectionCard[];
  isSelectionMode?: boolean;
  selectedCardIds?: Set<string>;
  onToggleCard?: (cardId: string) => void;
}

export default function CollectionGrid({ cards, isSelectionMode, selectedCardIds, onToggleCard }: CollectionGridProps): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10">
      {cards.map((item) => (
        <CollectionGridCard
          key={item.id}
          item={item}
          isSelectionMode={isSelectionMode}
          isSelected={selectedCardIds?.has(item.id)}
          onToggleCard={onToggleCard}
        />
      ))}
    </div>
  );
}

interface CollectionGridCardProps {
  item: EnrichedCollectionCard;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleCard?: (cardId: string) => void;
}

/**
 * Individual grid card - memoized to prevent unnecessary re-renders
 * Only re-renders when card ID, quantity, finish, or card data changes
 */
const CollectionGridCard = memo(function CollectionGridCard({
  item,
  isSelectionMode,
  isSelected,
  onToggleCard,
}: CollectionGridCardProps): ReactElement {
  const { selectCard } = useSelectedCard();
  const imageUris = getDisplayImageUris(item.card);

  // Starred functionality
  const oracleId = getOracleId(item.card);
  const isStarred = useIsStarred(oracleId ?? undefined);
  const toggleStar = useToggleStar();

  const handleClick = () => {
    if (isSelectionMode) {
      onToggleCard?.(item.id);
    } else {
      selectCard(item.id);
    }
  };

  return (
    <CardHoverPreview imageUris={imageUris} name={item.card.name} layout={item.card.layout} cardFaces={item.card.card_faces}>
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        className={clsx('group card-glow relative cursor-pointer rounded-lg', isSelectionMode && isSelected && 'ring-aether ring-2')}
      >
        <FlippableCardImage
          imageUris={imageUris}
          layout={item.card.layout}
          cardFaces={item.card.card_faces}
          name={item.card.name}
          size="small"
          sourceSize="normal"
          className="aspect-5/7 h-auto w-full"
        />

        {/* Selection checkbox overlay */}
        {isSelectionMode && (
          <div className="absolute top-1 left-1 z-10">
            {isSelected ? (
              <IoCheckbox className="text-aether h-6 w-6 drop-shadow-md" />
            ) : (
              <IoSquareOutline className="text-pearl/70 h-6 w-6 drop-shadow-md" />
            )}
          </div>
        )}

        {/* Quantity badge */}
        {item.quantity > 1 && <QuantityBadge quantity={item.quantity} className="absolute top-1 right-1" />}

        {/* Foil indicator */}
        {item.finish !== 'nonfoil' && (
          <div
            className={clsx(
              'bg-gold/90 text-void absolute rounded px-1.5 py-0.5 text-[10px] font-medium',
              isSelectionMode ? 'top-1 left-8' : 'top-1 left-1',
            )}
          >
            {item.finish === 'foil' ? 'F' : 'E'}
          </div>
        )}

        {/* Star toggle */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleStar(oracleId ?? '', item.card.name, item.scryfall_id);
          }}
          className="bg-void/80 absolute bottom-1 left-1 z-10 rounded-full p-1 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label={isStarred ? 'Remove from starred' : 'Add to starred'}
        >
          {isStarred ? <IoStar className="text-gold h-4 w-4" /> : <IoStarOutline className="text-ash hover:text-gold h-4 w-4" />}
        </button>

        {/* Hover overlay */}
        <div className="bg-void/60 pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg opacity-0 transition-opacity group-hover:opacity-100">
          <span className="text-pearl line-clamp-2 px-2 text-center text-xs font-medium">{item.card.name}</span>
        </div>
      </div>
    </CardHoverPreview>
  );
});
